import React, { useEffect, useRef, useState } from "react";
import api, { API_BASE, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Sparkles, Download, Loader2, FileSearch,
  TrendingDown, TrendingUp, CheckCircle2, AlertTriangle, RotateCw, AlertOctagon, Mail, FileBarChart2,
} from "lucide-react";
import RefundEmailModal from "@/components/RefundEmailModal";

const MODELS = [
  { v: "claude-sonnet-4-5", label: "Claude Sonnet 4.5" },
  { v: "gpt-5.2", label: "GPT-5.2" },
];

function HealthScoreBadge({ score }) {
  const s = Number(score) || 0;
  const color =
    s >= 70 ? "text-forest bg-forest/10 border-forest/30"
            : s >= 45 ? "text-ochre bg-ochre/10 border-ochre/30"
                      : "text-destructive bg-destructive/10 border-destructive/30";
  const label = s >= 70 ? "Healthy" : s >= 45 ? "Watch" : "Stressed";
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${color}`} data-testid="health-badge">
      <span className="num font-bold">{s}</span>
      <span>· {label}</span>
    </div>
  );
}

function StatementsList({ items, onDelete, selected, onToggle }) {
  if (items.length === 0) {
    return (
      <div className="card-surface p-12 text-center" data-testid="statements-empty">
        <FileSearch className="w-8 h-8 text-ink-lighter mx-auto mb-3" strokeWidth={1.5} />
        <div className="text-ink-light mb-1 font-medium">No statements yet</div>
        <div className="text-sm text-ink-lighter">Upload a bank-statement PDF to get started.</div>
      </div>
    );
  }
  return (
    <div className="card-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-paper">
          <tr className="text-left text-xs uppercase tracking-wider text-ink-lighter border-b border-border">
            <th className="px-5 py-3 w-10"></th>
            <th className="px-5 py-3">Statement</th>
            <th className="px-5 py-3">Period</th>
            <th className="px-5 py-3 text-right">Transactions</th>
            <th className="px-5 py-3 text-right">Cash In</th>
            <th className="px-5 py-3 text-right">Cash Out</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-border last:border-0 hover:bg-paper/60" data-testid={`statement-row-${s.id}`}>
              <td className="px-5 py-3">
                <input type="checkbox" checked={selected.includes(s.id)}
                  onChange={() => onToggle(s.id)} className="accent-forest"
                  data-testid={`stmt-checkbox-${s.id}`} />
              </td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-forest shrink-0" strokeWidth={1.5} />
                  <span className="font-medium truncate max-w-xs">{s.filename}</span>
                </div>
              </td>
              <td className="px-5 py-3 num text-xs text-ink-light whitespace-nowrap">{s.period_start} → {s.period_end}</td>
              <td className="px-5 py-3 num text-right">{s.transaction_count}</td>
              <td className="px-5 py-3 num text-right text-forest">{fmtMoney(s.total_inflow)}</td>
              <td className="px-5 py-3 num text-right text-ochre">{fmtMoney(s.total_outflow)}</td>
              <td className="px-5 py-3 text-right">
                <button onClick={() => onDelete(s.id)} className="text-ink-lighter hover:text-destructive" data-testid={`delete-stmt-${s.id}`} aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportCard({ report: initialReport, onDelete }) {
  const [report, setReport] = useState(initialReport);
  const [generatingPnl, setGeneratingPnl] = useState(false);
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = `${baseUrl}/api/statements/reports/${report.id}/pdf`;
  const pnlPdfUrl = `${baseUrl}/api/statements/reports/${report.id}/pnl/pdf`;
  const hasPnl = !!report.pnl;
  const a = report.analysis || {};
  const t = report.totals || {};
  const f = report.fees || {};
  const hasFees = (f.fees_grand_count || 0) > 0;

  const generatePnl = async () => {
    setGeneratingPnl(true);
    try {
      const { data } = await api.post(`/statements/reports/${report.id}/regenerate-pnl`);
      setReport({ ...report, pnl: data.pnl, period_start_date: data.period_start_date, period_end_date: data.period_end_date });
      toast.success("P&L generated — you can now download the PDF");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate P&L");
    } finally {
      setGeneratingPnl(false);
    }
  };

  return (
    <div className="card-surface card-hover p-6" data-testid={`report-card-${report.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="overline mb-1.5">Report · {report.model}</div>
          <h3 className="font-heading text-xl font-medium tracking-tight" data-testid={`report-title-${report.id}`}>
            {report.period_start} → {report.period_end}
          </h3>
          <div className="text-xs text-ink-lighter mt-1">
            {report.statement_count} statement{report.statement_count > 1 ? "s" : ""} ·
            {" "}{report.transaction_count} txns ·
            {" "}generated {fmtDate(report.generated_at)}
          </div>
        </div>
        <HealthScoreBadge score={a.health_score} />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4 text-center">
        <div className="bg-paper rounded-lg p-3">
          <div className="overline text-[9px] mb-0.5">Cash In</div>
          <div className="num text-base font-medium text-forest">{fmtMoney(t.inflow)}</div>
        </div>
        <div className="bg-paper rounded-lg p-3">
          <div className="overline text-[9px] mb-0.5">Cash Out</div>
          <div className="num text-base font-medium text-ochre">{fmtMoney(t.outflow)}</div>
        </div>
        <div className="bg-paper rounded-lg p-3">
          <div className="overline text-[9px] mb-0.5">Net</div>
          <div className={`num text-base font-medium ${(t.net || 0) >= 0 ? "text-forest" : "text-destructive"}`}>{fmtMoney(t.net)}</div>
        </div>
      </div>

      {hasFees && (
        <div className="bg-ochre/10 border border-ochre/25 rounded-lg p-3 mb-4 flex items-center justify-between gap-3" data-testid={`report-fees-${report.id}`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <AlertOctagon className="w-4 h-4 text-ochre shrink-0" strokeWidth={1.75} />
            <div className="text-sm min-w-0">
              <div className="font-medium text-ink truncate">Bank fees & NSF in this period</div>
              <div className="text-xs text-ink-light">
                {f.nsf_count} NSF · {f.bank_fees_count} other fee{f.bank_fees_count === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="num text-base font-medium text-ochre shrink-0">{fmtMoney(f.fees_grand_total)}</div>
        </div>
      )}

      {a.executive_summary && (
        <p className="text-sm text-ink-light leading-relaxed mb-4 line-clamp-4">{a.executive_summary}</p>
      )}

      {(a.quick_wins || []).length > 0 && (
        <div className="mb-4">
          <div className="overline mb-2">Top quick wins</div>
          <ul className="space-y-1.5">
            {a.quick_wins.slice(0, 3).map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-forest mt-1 shrink-0" strokeWidth={2} />
                <span><span className="font-medium text-ink">{q.title}</span> <span className="text-ink-lighter">· {fmtMoney(q.impact_per_month)}/mo</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-border gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <a href={pdfUrl} target="_blank" rel="noreferrer"
            data-testid={`download-pdf-${report.id}`}
            className="btn-forest text-sm inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Download full PDF
          </a>
          {hasPnl ? (
            <a href={pnlPdfUrl} target="_blank" rel="noreferrer"
              data-testid={`download-pnl-pdf-${report.id}`}
              className="btn-secondary text-sm inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> P&amp;L PDF
            </a>
          ) : (
            <button onClick={generatePnl} disabled={generatingPnl}
              data-testid={`generate-pnl-${report.id}`}
              className="btn-secondary text-sm inline-flex items-center gap-2">
              {generatingPnl ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBarChart2 className="w-4 h-4" />}
              {generatingPnl ? "Generating…" : "Generate P&L"}
            </button>
          )}
        </div>
        <button onClick={() => onDelete(report.id)} className="text-xs text-ink-lighter hover:text-destructive inline-flex items-center gap-1.5" data-testid={`delete-report-${report.id}`}>
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );
}

export default function Statements() {
  const [statements, setStatements] = useState([]);
  const [reports, setReports] = useState([]);
  const [fees, setFees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState("");
  const [uploadElapsed, setUploadElapsed] = useState(0); // seconds, for progress bar
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState([]);
  const [model, setModel] = useState("claude-sonnet-4-5");
  const [showRefund, setShowRefund] = useState(false);
  const [recoverySummary, setRecoverySummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [uploadAccountId, setUploadAccountId] = useState("");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, f, rec, ba] = await Promise.all([
        api.get("/statements"),
        api.get("/statements/reports"),
        api.get("/statements/fees-overview"),
        api.get("/statements/fees-recovered"),
        api.get("/bank-accounts").catch(() => ({ data: { accounts: [] } })),
      ]);
      setStatements(s.data);
      setReports(r.data);
      setFees(f.data);
      setRecoverySummary(rec.data);
      const accounts = ba.data?.accounts || [];
      setBankAccounts(accounts);
      // Pre-select the primary account if the user has one
      const primary = accounts.find((a) => a.is_primary);
      if (primary && !uploadAccountId) setUploadAccountId(primary.id);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    setUploading(true);
    setUploadElapsed(0);
    const startedAt = Date.now();
    const tick = setInterval(() => {
      setUploadElapsed((Date.now() - startedAt) / 1000);
    }, 200);
    let ok = 0;
    for (const f of files) {
      // Phased copy so users know what's happening through long uploads.
      setUploadPhase("Reading PDF…");
      const phaseTimer1 = setTimeout(() => setUploadPhase("Asking AI to parse…"), 4000);
      const phaseTimer2 = setTimeout(() => setUploadPhase("OCR fallback — scanning pages…"), 15000);
      const phaseTimer3 = setTimeout(() => setUploadPhase("Almost done…"), 35000);
      const fd = new FormData();
      fd.append("file", f);
      try {
        const params = uploadAccountId ? { bank_account_id: uploadAccountId } : {};
        await api.post("/statements/upload", fd, { timeout: 120000, params });
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${formatApiErrorDetail(e.response?.data?.detail)}`);
      } finally {
        clearTimeout(phaseTimer1);
        clearTimeout(phaseTimer2);
        clearTimeout(phaseTimer3);
      }
    }
    clearInterval(tick);
    setUploadPhase("");
    setUploadElapsed(0);
    if (ok > 0) toast.success(`Uploaded ${ok} statement${ok > 1 ? "s" : ""}`);
    setUploading(false);
    load();
  };

  const removeStatement = async (id) => {
    if (!confirm("Delete this statement and its transactions?")) return;
    try {
      await api.delete(`/statements/${id}`);
      toast.success("Statement deleted");
      setSelected(selected.filter((x) => x !== id));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeReport = async (id) => {
    if (!confirm("Delete this report?")) return;
    try {
      await api.delete(`/statements/reports/${id}`);
      setReports(reports.filter((r) => r.id !== id));
      toast.success("Report deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggle = (id) => {
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const selectAll = () => {
    setSelected(selected.length === statements.length ? [] : statements.map((s) => s.id));
  };

  const generate = async () => {
    if (statements.length === 0) {
      toast.error("Upload a statement first");
      return;
    }
    setGenerating(true);
    try {
      const payload = { model };
      if (selected.length > 0) payload.statement_ids = selected;
      const { data } = await api.post("/statements/report", payload, { timeout: 120000 });
      toast.success("AI report ready");
      setReports([data, ...reports]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const totalIn = statements.reduce((s, x) => s + x.total_inflow, 0);
  const totalOut = statements.reduce((s, x) => s + x.total_outflow, 0);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="statements-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Bank Statements</div>
          <h1 className="font-heading text-4xl font-medium tracking-tight">Drop in. Get a CFO read.</h1>
          <p className="text-ink-light mt-2">Upload one or many statements — Claude or GPT will return a full cashflow report with quick wins and tax angles.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          {bankAccounts.length > 0 && (
            <label className="text-xs">
              <div className="text-ink-light mb-1">Tag as</div>
              <select
                value={uploadAccountId}
                onChange={(e) => setUploadAccountId(e.target.value)}
                disabled={uploading}
                data-testid="statements-upload-account-select"
                className="bg-white border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none min-w-[200px]"
              >
                <option value="">— Unassigned —</option>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.mask ? ` · ••${a.mask}` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden"
            onChange={(e) => e.target.files && upload(Array.from(e.target.files))}
            data-testid="statements-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="btn-secondary relative inline-flex items-center gap-2 min-w-[200px] justify-center overflow-hidden" data-testid="upload-statement-button">
            {uploading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> <span data-testid="upload-phase">{uploadPhase || "Parsing…"}</span></>
              : <><Upload className="w-4 h-4" /> Upload PDF(s)</>}
            {uploading && (
              <span
                data-testid="upload-progress-bar"
                aria-hidden="true"
                className="absolute left-0 bottom-0 h-[3px] bg-forest/80 transition-[width] duration-200 ease-out"
                style={{
                  // Asymptotic curve: approaches 95% as elapsed grows; expected ~30s, never visually completes until done.
                  width: `${Math.min(95, (1 - Math.exp(-uploadElapsed / 18)) * 100).toFixed(1)}%`,
                }}
              />
            )}
          </button>
        </div>
      </div>

      {statements.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="card-surface p-4 text-center">
            <div className="overline mb-1">Statements</div>
            <div className="num text-2xl font-heading font-medium">{statements.length}</div>
          </div>
          <div className="card-surface p-4 text-center">
            <div className="overline mb-1">Total Cash In</div>
            <div className="num text-2xl font-heading font-medium text-forest">{fmtMoney(totalIn)}</div>
          </div>
          <div className="card-surface p-4 text-center">
            <div className="overline mb-1">Total Cash Out</div>
            <div className="num text-2xl font-heading font-medium text-ochre">{fmtMoney(totalOut)}</div>
          </div>
          <div className="card-surface p-4 text-center" data-testid="fees-stat-box">
            <div className="overline mb-1" style={{ color: "#D97736" }}>Fees & NSF</div>
            <div className="num text-2xl font-heading font-medium text-ochre" data-testid="fees-stat-amount">
              {fmtMoney(fees?.fees_grand_total || 0)}
            </div>
            <div className="text-[10px] text-ink-lighter mt-1">
              {fees?.fees_grand_count || 0} charge{(fees?.fees_grand_count || 0) === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      )}

      {fees && (fees.fees_grand_count > 0) && (
        <div className="card-surface p-5 mb-6 border-l-4 border-l-ochre" data-testid="fees-callout">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-ochre/15 text-ochre flex items-center justify-center shrink-0">
              <AlertOctagon className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <h3 className="font-heading text-lg font-medium">Bank fees & NSF charges</h3>
                <div className="num text-xl font-heading font-medium text-ochre">{fmtMoney(fees.fees_grand_total)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div className="bg-paper rounded-md px-3 py-2">
                  <div className="overline text-[9px] mb-0.5 text-destructive">NSF / Overdraft</div>
                  <div className="num font-medium" data-testid="fees-nsf-total">{fmtMoney(fees.nsf_total)}</div>
                  <div className="text-[10px] text-ink-lighter">{fees.nsf_count} event{fees.nsf_count === 1 ? "" : "s"}</div>
                </div>
                <div className="bg-paper rounded-md px-3 py-2">
                  <div className="overline text-[9px] mb-0.5">Other bank fees</div>
                  <div className="num font-medium" data-testid="fees-other-total">{fmtMoney(fees.bank_fees_total)}</div>
                  <div className="text-[10px] text-ink-lighter">{fees.bank_fees_count} charge{fees.bank_fees_count === 1 ? "" : "s"}</div>
                </div>
              </div>
              <p className="text-xs text-ink-light mb-3">
                Many of these are <span className="font-medium text-ink">refundable</span> — call your bank and ask for a courtesy reversal. Banks waive 1-2 per year on request.
              </p>
              <button
                onClick={() => setShowRefund(true)}
                data-testid="open-refund-modal"
                className="btn-forest text-sm inline-flex items-center gap-2 mb-3"
              >
                <Mail className="w-4 h-4" /> Draft refund email
              </button>
              {recoverySummary && recoverySummary.total_ytd > 0 && (
                <div className="inline-flex items-center gap-2 ml-2 text-xs bg-forest/10 text-forest px-3 py-1.5 rounded-full font-medium align-top" data-testid="recovered-badge">
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                  <span>{fmtMoney(recoverySummary.total_ytd)} recovered YTD</span>
                </div>
              )}
              {fees.recent_charges?.length > 0 && (
                <div className="space-y-1.5 pt-3 border-t border-border">
                  <div className="overline mb-2">Recent charges</div>
                  {fees.recent_charges.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs" data-testid={`fee-charge-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider shrink-0 ${c.kind === "nsf" ? "bg-destructive/10 text-destructive" : "bg-ochre/10 text-ochre"}`}>
                          {c.kind}
                        </span>
                        <span className="num text-ink-lighter">{fmtDate(c.date)}</span>
                        <span className="truncate">{c.description}</span>
                      </div>
                      <span className="num font-medium text-ochre shrink-0 ml-2">{fmtMoney(c.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {fees && fees.fees_grand_count === 0 && statements.length > 0 && (
        <div className="card-surface p-4 mb-6 flex items-center gap-3" data-testid="fees-clean">
          <div className="w-8 h-8 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="text-sm">
            <span className="font-medium text-ink">No bank fees or NSF charges detected.</span>
            <span className="text-ink-light ml-1.5">Clean run — keep that buffer healthy.</span>
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-heading text-xl font-medium">Uploaded statements</h2>
        {statements.length > 0 && (
          <button onClick={selectAll} className="text-xs text-ink-light hover:text-ink underline" data-testid="select-all-statements">
            {selected.length === statements.length ? "Unselect all" : "Select all"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="card-surface p-8 text-ink-lighter">Loading…</div>
      ) : (
        <StatementsList items={statements} onDelete={removeStatement} selected={selected} onToggle={toggle} />
      )}

      {statements.length > 0 && (
        <div className="mt-6 card-surface p-6" data-testid="generate-card">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="overline mb-1.5">AI report</div>
              <h3 className="font-heading text-2xl font-medium">Generate cashflow analysis</h3>
              <p className="text-sm text-ink-light mt-2">
                {selected.length > 0
                  ? `Will analyze ${selected.length} selected statement${selected.length > 1 ? "s" : ""}.`
                  : `Will analyze all ${statements.length} statement${statements.length > 1 ? "s" : ""}.`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-paper rounded-lg p-1 border border-border" data-testid="report-model-toggle">
                {MODELS.map((m) => (
                  <button key={m.v} onClick={() => setModel(m.v)}
                    data-testid={`report-model-${m.v}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      model === m.v ? "bg-white text-forest shadow-sm" : "text-ink-light"
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>
              <button onClick={generate} disabled={generating}
                data-testid="generate-report-button"
                className="btn-forest inline-flex items-center gap-2 disabled:opacity-60">
                {generating
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing… (~30s)</>
                  : <><Sparkles className="w-4 h-4" /> Generate report</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 mb-3 flex items-center justify-between">
        <h2 className="font-heading text-xl font-medium">Reports</h2>
        {reports.length > 0 && (
          <button onClick={load} className="text-xs text-ink-light hover:text-ink inline-flex items-center gap-1.5" data-testid="refresh-reports">
            <RotateCw className="w-3.5 h-3.5" /> Refresh
          </button>
        )}
      </div>
      {reports.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="reports-empty">
          <Sparkles className="w-8 h-8 text-ink-lighter mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-ink-light mb-1 font-medium">No reports yet</div>
          <div className="text-sm text-ink-lighter">Upload statements, then click &ldquo;Generate report&rdquo;.</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="reports-grid">
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} onDelete={removeReport} />
          ))}
        </div>
      )}

      <RefundEmailModal open={showRefund} onClose={() => setShowRefund(false)} onRecoveryLogged={() => load()} />
    </div>
  );
}
